import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Renders the result of a completeness-review check.
 *
 * The component deliberately does not create or alter review copy. The
 * finalizer owns the boolean and explanation; this component only presents
 * those values consistently wherever a result is shown.
 */
@Component({
  selector: 'itc-bge-completeness-pass-fail',
  standalone: true,
  template: `
    @if (hasResult) {
    <div
      class="bge-completeness-pass-fail"
      [ngClass]="statusClass"
      [class.bge-completeness-pass-fail--tooltip]="messageDisplay === 'tooltip'"
      [attr.aria-label]="messageDisplay === 'inline' ? ariaLabel : null"
    >
      @if (messageDisplay === 'tooltip' && message) {
      <span
        class="bge-completeness-pass-fail__tooltip-trigger"
        tabindex="0"
        [attr.aria-label]="ariaLabel"
        [matTooltip]="message"
        matTooltipPosition="above"
        [matTooltipShowDelay]="250"
      >
        <mat-icon aria-hidden="true">{{ iconName }}</mat-icon>
      </span>
      } @else {
      <mat-icon aria-hidden="true">{{ iconName }}</mat-icon>
      @if (message) {
      <span>{{ message }}</span>
      } }
    </div>
    }
  `,
  styleUrls: ['./bge-completeness-pass-fail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
})
export class BgeCompletenessPassFailComponent {
  @Input() passed: boolean | null | undefined;
  @Input() message: string | null | undefined;
  @Input() accessibleMessage: string | null | undefined;
  @Input() messageDisplay: 'inline' | 'tooltip' = 'inline';

  get hasResult(): boolean {
    return this.passed !== undefined;
  }

  get iconName(): string {
    if (this.passed === true) {
      return 'check_circle';
    }

    if (this.passed === false) {
      return 'error';
    }

    return 'do_not_disturb_on';
  }

  get statusClass(): string {
    if (this.passed === true) {
      return 'bge-completeness-pass-fail--pass';
    }

    if (this.passed === false) {
      return 'bge-completeness-pass-fail--fail';
    }

    return 'bge-completeness-pass-fail--unknown';
  }

  get ariaLabel(): string {
    const detail = this.accessibleMessage ?? this.message;
    return detail ? `${this.statusLabel}: ${detail}` : this.statusLabel;
  }

  private get statusLabel(): string {
    if (this.passed === true) {
      return 'Passed';
    }

    if (this.passed === false) {
      return 'Failed';
    }

    return 'Status unavailable';
  }
}
